import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress, Table, TableHead, TableBody,
  TableRow, TableCell, Chip,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Download as DownloadIcon, Inbox as InboxIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useEnvironment } from '../contexts/EnvironmentContext';

const API_URL = process.env.REACT_APP_API_URL;

const when = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

// The submitted fields, minus the internal flow_token Meta includes in every response.
const parseAnswers = (raw) => {
  try {
    const { flow_token, ...rest } = JSON.parse(raw || '{}');
    return rest;
  } catch {
    return {};
  }
};

const csvEscape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

/**
 * Captured leads, every submitted flow, newest first, with its Click-to-WhatsApp ad
 * attribution, plus a CSV export to hand off to a CRM / calendar / spreadsheet.
 */
const LeadsPage = () => {
  const { authenticatedFetch, isDemoMode } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const envId = selectedEnvironment?.id;

  const load = useCallback(async () => {
    if (!envId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch(`${API_URL}/api/environments/${envId}/leads`);
      if (!res.ok) throw new Error(`Failed to load (${res.status})`);
      setRows(await res.json());
    } catch (err) {
      setError(err.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, envId]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    const parsed = rows.map((r) => ({ ...r, answersObj: parseAnswers(r.answers) }));
    const answerKeys = [...new Set(parsed.flatMap((r) => Object.keys(r.answersObj)))];
    const headers = ['contact', 'submittedAt', 'ad', 'adId', 'ctwaClid', ...answerKeys];
    const lines = [headers.map(csvEscape).join(',')];
    for (const r of parsed) {
      lines.push([
        r.contact, r.submittedAt, r.adHeadline, r.adId, r.ctwaClid,
        ...answerKeys.map((k) => r.answersObj[k]),
      ].map(csvEscape).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <InboxIcon color="primary" />
        <Typography variant="h5" fontWeight="bold" sx={{ flexGrow: 1 }}>
          Leads
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={downloadCsv}
          disabled={rows.length === 0}
        >
          Export CSV
        </Button>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Everyone who completed one of your flows, with the ad that brought them in. Export to
        drop them into your CRM, calendar or spreadsheet.
      </Typography>

      {isDemoMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Example data</strong>, this is what the page looks like with leads in it. Your
          own appear here as customers complete your flows.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!envId ? (
        <Alert severity="info">Select an environment to see its leads.</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">
          No leads yet. They appear here as customers complete your flows.
        </Alert>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Contact</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>From ad</TableCell>
                <TableCell>Answers</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => {
                const answers = parseAnswers(r.answers);
                const entries = Object.entries(answers);
                return (
                  <TableRow key={`${r.contact}-${r.submittedAt}-${i}`} hover>
                    <TableCell>{r.contact}</TableCell>
                    <TableCell><Typography variant="caption">{when(r.submittedAt)}</Typography></TableCell>
                    <TableCell>
                      {r.adId
                        ? <Chip size="small" variant="outlined" label={r.adHeadline || r.adId} />
                        : <Typography variant="caption" color="text.secondary">Organic</Typography>}
                    </TableCell>
                    <TableCell>
                      {entries.length === 0
                        ? <Typography variant="caption" color="text.secondary">-</Typography>
                        : entries.map(([k, v]) => (
                          <Typography key={k} variant="caption" display="block">
                            <b>{k}:</b> {String(v)}
                          </Typography>
                        ))}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default LeadsPage;
